import { Visibility } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationModal from '../../../common/ConfirmationModal';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import api from '../../../../services/httpClient';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import { SYSTEM_ENDPOINTS } from '../../../../services/endpoints/system.endpoints';
import Alert from '../../../common/Alert';
import { AsyncAutocomplete, Option } from '../../../common/AsyncAutocomplete';
import Button from '../../../common/Button';
import CloseIcon from '../../../common/icons/CloseIcon';
import Loading from '../../../common/Loading';
import TextField from '../../../common/TextField';
import JobDescriptionPopup from './JobDescriptionPopup';
import type { VacancyApiResponse } from '../../../../services/types/vacancy.types';
import { formatNumberWithCommas, removeCommas } from '../../../../utils/formatting';
import { buildJDFormData } from '../../../../utils/vacancyFormData';
import { validateJDForm } from '../../../../utils/vacancyValidation';

const buttonStyle = {
  '&.MuiButton-contained': {
    backgroundColor: '#E95845',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#E95845',
    },
  },
};

interface VacancyJDFormProps {
  type: 'add' | 'edit';
  addedVacancyId?: number | null;
  onCancel: () => void;
  onSuccess: (message?: string) => void;
  onError: (message?: string) => void;
}

const VacancyJDForm: React.FC<VacancyJDFormProps> = ({
  type,
  addedVacancyId,
  onCancel,
  onSuccess,
  onError,
}) => {
  const params = useParams();
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [aboutClient, setAboutClient] = useState('');
  const [aboutEyepax, setAboutEyepax] = useState('');
  const [description, setDescription] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [isJDInProgress, setIsJDInProgress] = useState(false);
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [openJobDescription, setOpenJobDescription] = useState(false);
  const [locations, setLocations] = useState<{ 
    city: Option | null; 
    country: Option | null;
    budgetAmount: string;
    budgetCurrency: string;
  }[]>([
    { city: null, country: null, budgetAmount: '', budgetCurrency: 'USD' },
  ]);
  
  // Confirmation modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [lastIsGenerateJD, setLastIsGenerateJD] = useState(false);

  useEffect(() => {
    if (
      (type === 'add' && pageId !== 8) ||
      (type === 'edit' && pageId !== 11)
    ) {
      setPageId(type === 'add' ? 8 : 11);
      return;
    }

    const vacancyId = params?.id;
    if (type === 'add' || vacancyId === undefined || vacancyId === null) return;

    setIsLoading(true);
    api
      .get<VacancyApiResponse>(VACANCY_ENDPOINTS.byId(vacancyId), {
        headers: { ...API_HEADERS.VACANCY_FORM_DEFAULT },
      })
      .then(async (response) => {
        const data = response.data;

        setTitle(data.vacancyTitle);
        setAboutClient(data.vacancyAboutClient);
        setAboutEyepax(data.vacancyAboutEyepax);
        setJobDescription(
          data.jobDescription ? data.jobDescription : data.jobDescriptionAI,
        );
        setDescription(data.description);
        setIsJDInProgress(data.jdInProgress || false);

        // Load locations with country currency
        if (data.locations?.length) {
          const locationsWithCurrency = await Promise.all(
            data.locations.map(async (location) => {
              let countryCurrency: string | null = null;

              // Fetch country currency if country ID exists
              if (location?.country?.id) {
                const countryId = location.country.id;
                try {
                  const countryResponse = await api.get<{
                    data: { id: number; name: string; currency: string }[];
                  }>(SYSTEM_ENDPOINTS.countries, {
                    headers: { ...API_HEADERS.COUNTRIES_LOOKUP },
                  });
                  const countryData = countryResponse.data.data.find(
                    (c: any) => c.id === countryId,
                  );
                  if (
                    countryData?.currency &&
                    countryData.currency.trim() !== ""
                  ) {
                    countryCurrency = countryData.currency;
                  }
                } catch (error) {
                  console.error('Error fetching country currency:', error);
                }
              }

              // Priority: country currency (if exists and not empty) > stored budgetCurrency > USD
              const finalCurrency =
                countryCurrency && countryCurrency.trim() !== ''
                  ? countryCurrency
                  : location?.budgetCurrency &&
                      location.budgetCurrency.trim() !== ''
                    ? location.budgetCurrency
                    : 'USD';

              return {
                city: {
                  value: location?.city?.id.toString() || '',
                  label: location?.city?.name || '',
                },
                country: {
                  value: location?.country?.id.toString() || '',
                  label: location?.country?.name || '',
                  currency: countryCurrency || '',
                },
                budgetAmount: location?.budgetAmount
                  ? formatNumberWithCommas(location.budgetAmount)
                  : '',
                budgetCurrency: finalCurrency,
              };
            }),
          );
          setLocations(locationsWithCurrency);
        } else {
          setLocations([
            {
              city: null,
              country: null,
              budgetAmount: '',
              budgetCurrency: 'USD',
            },
          ]);
        }
        setIsLoading(false);
      });
  }, [type, params.id, pageId, setPageId]);

  const buildFormData = (isGenerateJD: boolean): FormData =>
    buildJDFormData(
      aboutClient,
      aboutEyepax,
      jobDescription,
      description,
      locations,
      isGenerateJD,
    );

  const handleSave = async (isGenerateJD: boolean) => {
    const newErrors = validateJDForm(locations, aboutEyepax, jobDescription);

    setErrors(newErrors);
    if (Object.keys(newErrors).length >= 1) {
      // If there's a job description error, open the popup to show the error
      if (newErrors.jobDescription) {
        setOpenJobDescription(true);
      }
      onError('Please fix the errors before saving.');
      return;
    }

    try {
      setIsSaving(true);
      setLastIsGenerateJD(isGenerateJD);
      setIsJDInProgress(isGenerateJD);
      const vacancyId = addedVacancyId || params.id;
      if (!vacancyId) return;

      const formData = buildFormData(isGenerateJD);
      // First call without forceUnpublish flag

      const response = await api.put<VacancyApiResponse & { requiresConfirmation?: boolean }>(
        VACANCY_ENDPOINTS.updateJD(vacancyId),
        formData,
        {
          headers: {
            ...(type === 'add' ? API_HEADERS.VACANCY_JD_SAVE_ADD : API_HEADERS.VACANCY_JD_SAVE_EDIT),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        // Check if confirmation is required
        if (response.data.requiresConfirmation) {
          setIsConfirmModalOpen(true);
          setIsSaving(false);
        } else {
          onSuccess('Job Description updated successfully!');
          setIsSaving(false);
        }
      }
    } catch (error) {
      console.error('Error updating vacancy:', error);
      onError('Failed to update vacancy. Please try again.');
      setIsSaving(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      setIsConfirming(true);
      const vacancyId = addedVacancyId || params.id;
      if (!vacancyId) return;

      // Rebuild formData with forceUnpublish flag
      const formData = buildFormData(lastIsGenerateJD);
      formData.append('forceUnpublish', 'true');

      const response = await api.put<VacancyApiResponse>(
        VACANCY_ENDPOINTS.updateJD(vacancyId),
        formData,
        {
          headers: {
            ...(type === 'add' ? API_HEADERS.VACANCY_JD_SAVE_ADD : API_HEADERS.VACANCY_JD_SAVE_EDIT),
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.status === 200) {
        // Always call deleteJob API to remove from website (same as world icon unpublish)
        try {
          await api.post(VACANCY_ENDPOINTS.deleteJob(vacancyId), {}, {
            headers: { ...API_HEADERS.VACANCY_SEARCH_DELETE_JOB },
          });
        } catch (deleteError) {
          console.error('Error unpublishing from website:', deleteError);
          // Continue anyway - the internal unpublish worked
        }
        
        setIsConfirmModalOpen(false);
        onSuccess('Job Description updated successfully! The vacancy has been unpublished and approval status is now pending.');
      }
    } catch (error: any) {
      console.error('Error confirming update:', error);
      onError('Failed to confirm update. Please try again.');
    } finally {
      setIsSaving(false);
      setIsConfirming(false);
    }
  };

  const handleLoadCities = async (query: string, countryId?: string) => {
    const response = await api.get<{ data: { id: number; city: string }[] }>(
      `${SYSTEM_ENDPOINTS.cities}?query=${encodeURIComponent(query)}&countryId=${countryId ?? ''}`,
      {
        headers: { ...API_HEADERS.COUNTRIES_LOOKUP },
      },
    );
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  };

  const handleLoadCountries = async (query: string) => {
    const response = await api.get<{
      data: { id: number; name: string; currency: string }[];
    }>(SYSTEM_ENDPOINTS.countries, {
      headers: { ...API_HEADERS.COUNTRIES_LOOKUP },
    });
    return response.data.data.map((item: any) => ({
      value: item.id.toString(),
      label: item.name,
      currency: item.currency || '',
    }));
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {isJDInProgress && (
            <Alert
              title="Info"
              message="Job Description Generation is in progress. Please wait for it to complete."
            />
          )}

          <Box className="edit-section">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                width: '1300px',
              }}
            >
              <div className="edit-section-title">Locations*</div>
              {locations.length < 4 && (
                <IconButton
                  className="edit-section-add-button"
                  onClick={() =>
                    setLocations([
                      ...locations,
                      {
                        city: null,
                        country: null,
                        budgetAmount: "",
                        budgetCurrency: "USD",
                      },
                    ])
                  }
                >
                  <AddIcon />
                </IconButton>
              )}
            </Box>
            {locations.map((location, index) => (
              <Box
                key={`${location.country?.value}-${location.city?.value}-${index}`}
                className="edit-sub-section"
                sx={{ width: "1300px", mb: 2, position: "relative" }}
              >
                <Box sx={{ display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                  <Box>
                    <AsyncAutocomplete
                      label="Country"
                      width="360px"
                      value={location.country}
                      loadOptions={handleLoadCountries}
                      onChange={(option) => {
                        const currency =
                          option?.currency &&
                          (option.currency as string).trim() !== ''
                            ? (option.currency as string)
                            : 'USD';
                        setLocations(
                          locations.map((loc, i) =>
                            i === index
                              ? {
                                  ...loc,
                                  country: option,
                                  budgetCurrency: currency,
                                  city: null,
                                }
                              : loc,
                          ),
                        );
                        setErrors({ ...errors, locations: '' });
                      }}
                    />
                  </Box>
                  <Box>
                    <AsyncAutocomplete
                      label="City"
                      width="360px"
                      value={location.city}
                      onChange={(option) => {
                        setLocations(
                          locations.map((loc, i) =>
                            i === index ? { ...loc, city: option } : loc,
                          ),
                        );
                        setErrors({ ...errors, locations: '' });
                      }}
                      loadOptions={(query) =>
                        handleLoadCities(query, location.country?.value)
                      }
                    />
                  </Box>
                  <Box>
                    <TextField
                      id={`jd-location-${index}-budget`}
                      name={`locations[${index}].budgetAmount`}
                      label={`Budget (${location.budgetCurrency || 'USD'})`}
                      type="text"
                      value={location.budgetAmount}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Remove all non-digit characters except for the decimal point
                        value = value.replace(/[^\d.]/g, '');
                        // Allow only one decimal point
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }
                        // Remove commas and format
                        const numericValue = removeCommas(value);
                        if (numericValue === '' || numericValue === '.') {
                          setLocations(
                            locations.map((loc, i) =>
                              i === index ? { ...loc, budgetAmount: "" } : loc,
                            ),
                          );
                        } else {
                          // Check if it ends with a decimal point (user might be typing)
                          const hasTrailingDecimal = numericValue.endsWith('.');
                          const numValue = hasTrailingDecimal
                            ? numericValue.slice(0, -1)
                            : numericValue;

                          if (numValue === '') {
                            setLocations(
                              locations.map((loc, i) =>
                                i === index
                                  ? { ...loc, budgetAmount: '' }
                                  : loc,
                              ),
                            );
                          } else {
                            const formatted = formatNumberWithCommas(numValue);
                            setLocations(
                              locations.map((loc, i) =>
                                i === index
                                  ? {
                                      ...loc,
                                      budgetAmount: hasTrailingDecimal
                                        ? formatted + '.'
                                        : formatted,
                                    }
                                  : loc,
                              ),
                            );
                          }
                        }
                      }}
                      placeholder={`Enter total salary cost in ${location.budgetCurrency || 'USD'}`}
                      sx={{
                        width: '360px',
                        '& .MuiOutlinedInput-input': {
                          textAlign: 'right',
                        },
                      }}
                    />
                  </Box>
                  {locations.length > 1 && (
                    <IconButton
                      onClick={() =>
                        setLocations(locations.filter((_, i) => i !== index))
                      }
                      className="edit-section-close-button"
                      sx={{ mt: 1 }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}
            {errors.locations && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ color: "#E90000", fontSize: "0.875rem" }}>
                  {errors.locations}
                </Typography>
              </Box>
            )}
          </Box>

          <Box className="edit-section">
            <Grid container spacing={4}>
              <Grid container size={12}>
                <Grid size={12}>
                  <TextField
                    label="Vacancy Description"
                    multiline
                    rows={10}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder='Please include any additional skills, technologies, or qualifications in this section, if applicable.'
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Box className="edit-section">
            <Grid container spacing={4}>
              {/* First Row */}
              <Grid container size={12}>
                <Grid size={12}>
                  <TextField
                    label="About Client"
                    multiline
                    rows={5}
                    value={aboutClient}
                    onChange={(e) => setAboutClient(e.target.value)}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                      },
                    }}
                  />
                </Grid>
              </Grid>
              {/* Second Row */}
              <Grid container size={12}>
                <Grid size={12}>
                  <TextField
                    label="About Eyepax"
                    name="aboutEyepax"
                    multiline
                    rows={5}
                    value={aboutEyepax}
                    onChange={(e) => setAboutEyepax(e.target.value)}
                    error={!!errors.aboutEyepax}
                    helperText={errors.aboutEyepax}
                    required={true}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        height: "auto",
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {((type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_JD_SAVE_ADD)) ||
                (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_JD_SAVE_EDIT))) && (
                <Button
                  width="231px"
                  variant="outlined"
                  startIcon={<Visibility />}
                  onClick={() => setOpenJobDescription(true)}
                  disabled={isSaving || isJDInProgress}
                >
                  View Job Description
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onCancel}
                className="edit-section-cancel-button"
              >
                Back
              </Button>
              {((type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_QUESTIONS_SAVE_ADD)) ||
                (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_QUESTIONS_SAVE_EDIT))) && (
                <Button
                  isLoading={isSaving}
                  variant="contained"
                  onClick={() => handleSave(false)}
                  className="edit-section-save-button"
                  disabled={isSaving || isJDInProgress}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              )}
              {((type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_JD_SECTION_ADD)) ||
                (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_JD_SECTION_EDIT))) && (
                <Button
                  variant="contained"
                  onClick={() => handleSave(true)}
                  width="231px"
                  sx={buttonStyle}
                  disabled={isSaving || isJDInProgress}
                >
                  Generate Job Description
                </Button>
              )}
            </Box>
          </Box>

          <JobDescriptionPopup
            type={type}
            open={openJobDescription}
            initialJobDescription={jobDescription}
            onClose={() => setOpenJobDescription(false)}
            onSave={(jobDescription) => {
              setOpenJobDescription(false);
              setJobDescription(jobDescription);
            }}
            title={title}
            subtitle="Job Description"
            error={errors.jobDescription}
          />
        </>
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        open={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
        }}
        onConfirm={handleConfirmUpdate}
        title="Unpublish Vacancy?"
        message="If you want to do these changes, the vacancy will be unpublished. Do you want to continue?"
        confirmText="Yes"
        cancelText="No"
        isActioning={isConfirming}
      />
    </Box>
  );
};

export default VacancyJDForm;
